import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, Building2, Users, Clock, MapPin } from "lucide-react";
import { trackApplyClick } from "@/lib/apply-cta";

const URL = "https://vendingmachinefree.com/qualify-for-free-vending-machine";
const TITLE = "How to Get a Free Vending Machine — Qualify in 30s";
const DESC =
  "How to get a free vending machine for your business — see the 4 eligibility requirements, apply online in 2 minutes, and get a 100% no-cost machine installed, restocked, and serviced nationwide.";

const FAQ = [
  {
    q: "How many employees do I need to qualify for a free vending machine?",
    a: "Most locations qualify with 25+ employees, students, patients, or daily visitors. High-traffic sites (gyms, hotels, waiting rooms) can qualify with fewer people if foot traffic exceeds 100 per day.",
  },
  {
    q: "Is the vending machine really 100% free?",
    a: "Yes. Vending Machine Free.com covers the machine, delivery, installation, restocking, servicing, cash handling, and repairs at no cost to qualifying businesses across the USA.",
  },
  {
    q: "What types of businesses qualify?",
    a: "Offices, warehouses, factories, hospitals, clinics, schools, universities, hotels, gyms, auto dealerships, laundromats, apartment complexes, distribution centers, and any location with steady foot traffic.",
  },
  {
    q: "How long does approval take?",
    a: "We respond within 4–6 hours during business hours. Site surveys are typically scheduled within 3 business days, and installation completes in 7–14 days after approval.",
  },
  {
    q: "Do I need to sign a long contract?",
    a: "No long-term lock-in. Our standard placement agreement is flexible and can be reviewed by your team before installation.",
  },
  {
    q: "Do you serve rural areas or only cities?",
    a: "We serve all 50 states — urban, suburban, and rural locations. Enter your ZIP code to instantly confirm coverage.",
  },
];

export const Route = createFileRoute("/qualify-for-free-vending-machine")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "FAQPage",
              mainEntity: FAQ.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            {
              "@type": "Service",
              name: "Free Vending Machine Qualification",
              provider: {
                "@type": "Organization",
                name: "Vending Machine Free.com",
                url: "https://vendingmachinefree.com",
              },
              areaServed: { "@type": "Country", name: "United States" },
              serviceType: "Free Vending Machine Placement Qualification",
              url: URL,
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://vendingmachinefree.com/" },
                { "@type": "ListItem", position: 2, name: "Qualify", item: URL },
              ],
            },
          ],
        }),
      },
    ],
  }),
  component: QualifyPage,
});

const CRITERIA = [
  { icon: Users, title: "25+ People On-Site", body: "Employees, students, patients, guests, or visitors daily." },
  { icon: Clock, title: "Regular Operating Hours", body: "Open at least 5 days/week — 24/7 sites are ideal." },
  { icon: Building2, title: "Indoor Placement Space", body: "A 3ft × 3ft area near a standard 110V outlet." },
  { icon: MapPin, title: "USA Location", body: "All 50 states covered — urban, suburban, and rural." },
];

function QualifyPage() {
  return (
    <div className="min-h-screen bg-background">
      <section className="bg-charcoal text-charcoal-foreground py-16">
        <div className="container-x max-w-4xl">
          <p className="text-xs uppercase tracking-[0.2em] text-safety">Free Placement Program</p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl uppercase leading-tight">
            How to Get a Free Vending Machine for Your Business
          </h1>
          <p className="mt-4 text-lg text-white/85 max-w-2xl">
            Follow the 4 eligibility steps below to qualify for a 100% free vending machine — no purchase, no lease, no service fees. Most US businesses qualify in under 30 seconds.
          </p>
          <Link
            to="/"
            hash="contact"
            onClick={() => trackApplyClick("qualify_hero")}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-safety px-6 py-3 font-semibold text-safety-foreground"
          >
            Check My ZIP Code <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <section className="py-16">
        <div className="container-x max-w-5xl">
          <h2 className="font-display text-3xl uppercase">The 4 Qualification Criteria</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {CRITERIA.map((c) => (
              <div key={c.title} className="rounded-lg border border-border bg-secondary p-6">
                <c.icon className="h-8 w-8 text-safety" />
                <h3 className="mt-3 font-display text-xl uppercase">{c.title}</h3>
                <p className="mt-2 text-muted-foreground">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-secondary">
        <div className="container-x max-w-4xl">
          <h2 className="font-display text-3xl uppercase">Frequently Asked Questions</h2>
          <div className="mt-6 space-y-4">
            {FAQ.map((f) => (
              <details key={f.q} className="rounded-lg border border-border bg-background p-5">
                <summary className="cursor-pointer font-semibold">{f.q}</summary>
                <p className="mt-3 text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container-x max-w-4xl">
          <h2 className="font-display text-2xl uppercase">Related Resources</h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2 text-safety">
            <li><a className="hover:underline" href="/free-vending-machines-usa">Free Vending Machines USA →</a></li>
            <li><a className="hover:underline" href="/free-vending-machine-placement">Free Placement Program →</a></li>
            <li><a className="hover:underline" href="/vending-machine-placement">Placement Overview →</a></li>
            <li><a className="hover:underline" href="/vending-machine-cost">Vending Machine Cost →</a></li>
            <li><Link className="hover:underline" to="/coverage">USA Coverage Map →</Link></li>
            <li><Link className="hover:underline" to="/about">About Us →</Link></li>
          </ul>
          <div className="mt-8 flex items-center gap-3">
            <CheckCircle2 className="h-6 w-6 text-safety" />
            <p className="text-sm text-muted-foreground">
              Powered by American businesses — get a Free Vending Machine Nationwide.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
